import { toaster } from '@/components/ui/toaster';
import { conversationQueryKeys } from '@/hooks/queries/conversation-query-keys';
import api from '@/libs/axiosInstance';
import type {
  ApiErrorResponse,
  ApiResponse,
  SendCreatorContactOutreachEmailResponse,
  SendCreatorOutreachEmailRequest,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { isPlanLimitError } from '@/libs/plan-limit-refusal';

export interface SendCreatorContactOutreachEmailParams extends SendCreatorOutreachEmailRequest {
  creatorIdentityId: string;
}

/**
 * Queue a contact-keyed creator outreach email: emails a creator the brand can
 * already reach (contact override or identity primary email) without saving it
 * to the wishlist. The recipient address is resolved server-side, so the client
 * only sends the contact id and the message.
 */
export function useSendCreatorContactOutreachEmail() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<
    SendCreatorContactOutreachEmailResponse,
    AxiosError<ApiErrorResponse>,
    SendCreatorContactOutreachEmailParams
  >({
    mutationFn: async ({
      creatorIdentityId,
      subject,
      body,
      attachmentIds,
    }): Promise<SendCreatorContactOutreachEmailResponse> => {
      const response = await api.post<ApiResponse<SendCreatorContactOutreachEmailResponse>>(
        `/dashboard/contacts/${creatorIdentityId}/outreach-email`,
        {
          subject,
          body,
          attachmentIds,
        }
      );

      if (!response.data.data) {
        throw new Error('Invalid response structure');
      }

      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationQueryKeys.root });
      toaster.create({
        title: t('creatorOutreach.send.queued.title'),
        description: t('creatorOutreach.send.queued.description'),
        type: 'success',
      });
    },
    onError: (error) => {
      // A plan refusal (a prospect whose address the plan withholds) opens the
      // upsell from the API client; a toast would repeat it.
      if (isPlanLimitError(error)) return;
      const errorCode = error.response?.data?.code;
      const fallback = error.response?.data?.message ?? t('creatorOutreach.send.error.generic');
      const translated = errorCode ? t(`errors.${errorCode}`, { defaultValue: '' }) : '';

      toaster.create({
        title: t('creatorOutreach.send.error.title'),
        description: translated || fallback,
        type: 'error',
        duration: 6000,
      });
    },
  });
}
